import { api, type RawResult } from '@/lib/api/client';
import type { AdminNumbers, NeedsActionDismissal, NeedsActionItem, NeedsActionReason, Pagination } from '@/lib/api/types';

/** `meta.totals`: every reason's count over the cohort filter alone (always all reasons). */
export type NeedsActionTotals = Partial<Record<NeedsActionReason, number>>;

export interface NeedsActionParams {
  reason?: NeedsActionReason[];
  cohort_id?: string;
  page?: number;
  per_page?: number;
  /** Also list the dismissed items (flagged); the totals never count them. */
  include_dismissed?: boolean;
}

/** The occurrence a dismissal hides: the row's own key, as the list returned it. */
export type NeedsActionOccurrence = Pick<
  NeedsActionItem,
  'reason' | 'enrolment_id' | 'student_id' | 'cohort_id' | 'subject_id' | 'since'
>;

export interface NeedsActionPage {
  rows: NeedsActionItem[];
  pagination: Pagination;
  totals: NeedsActionTotals;
}

/**
 * The cross-cohort reads (round 10, phase 24): Needs action and the admin-only
 * numbers. Both are `students:read`.
 */
export const workApi = {
  /**
   * One page of `GET /admin/needs-action`, oldest first. The page, its total and
   * the per-reason totals are one statement server-side.
   */
  async needsAction(p: NeedsActionParams): Promise<NeedsActionPage> {
    const res: RawResult<NeedsActionItem[]> = await api.getRaw<NeedsActionItem[]>('/admin/needs-action', {
      reason: p.reason && p.reason.length > 0 ? p.reason : undefined,
      cohort_id: p.cohort_id || undefined,
      page: p.page,
      per_page: p.per_page,
      include_dismissed: p.include_dismissed ? 'true' : undefined,
    });
    const rows = res.data ?? [];
    return {
      rows,
      pagination: res.meta?.pagination ?? { page: 1, per_page: p.per_page ?? 20, total: rows.length, total_pages: 1 },
      totals: (res.meta?.totals as NeedsActionTotals | undefined) ?? {},
    };
  },

  /**
   * `POST /admin/needs-action/dismissals` (round 16, B-R16-06): hide this one
   * occurrence. `students:write`. The same problem coming back later carries a
   * new `since` and shows again.
   */
  dismiss(item: NeedsActionOccurrence, note?: string): Promise<NeedsActionDismissal> {
    return api.post<NeedsActionDismissal>('/admin/needs-action/dismissals', {
      reason: item.reason,
      enrolment_id: item.enrolment_id,
      student_id: item.student_id,
      cohort_id: item.cohort_id,
      subject_id: item.subject_id,
      since: item.since,
      note: note?.trim() ? note.trim() : undefined,
    });
  },

  /** `DELETE /admin/needs-action/dismissals/{id}`: put the item back in the list. */
  restore(id: string): Promise<null> {
    return api.del(`/admin/needs-action/dismissals/${encodeURIComponent(id)}`);
  },

  /** `GET /admin/numbers`: never public, live (not cached). */
  numbers(): Promise<AdminNumbers> {
    return api.get<AdminNumbers>('/admin/numbers');
  },
};
